import React, { useEffect, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Breadcrumb,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Grid,
  Tag,
  Popconfirm,
  Avatar,
  Tooltip,
  Progress,
  Select,
  DatePicker
} from 'antd';
import { Link } from 'react-router-dom';
import {
  SearchOutlined,
  RightOutlined,
  SyncOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloudDownloadOutlined,
  PlusOutlined,
  GiftOutlined,
  ReloadOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CarOutlined,
  WifiOutlined,
  ClockCircleOutlined,
  UserSwitchOutlined,
  PhoneOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { synchronizationService, citiesService } from '../../common/services';
import Can from '../../components/can';
import dayjs from 'dayjs';

// Couleur avatar dynamique
const getAvatarColor = (name) => {
  const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa541c'];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

// Initiales
const getInitials = (nom, prenom) => {
  const n = nom?.charAt(0)?.toUpperCase() || '';
  const p = prenom?.charAt(0)?.toUpperCase() || '';
  return n + p || '?';
};

const SyncAnciensChauffeurs = () => {
  // Liste des chauffeurs Yandex non présents dans Tigoon
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // Villes
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);

  // Statistiques
  const [stats, setStats] = useState({
    totalYandex: 0,
    totalTigoon: 0,
    notInTigoon: 0,
    syncRate: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // Recherche + pagination + filtre date
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [dateRange, setDateRange] = useState(null);

  // Sync en cours
  const [syncingDriverId, setSyncingDriverId] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  // Charger les villes au montage
  useEffect(() => {
    citiesService.getAll()
      .then((res) => {
        const citiesData = res?.data || [];
        const activeCities = citiesData.filter(c => c.isActive);
        setCities(activeCities);
        const defaultCity = activeCities.find(c => c.isDefault);
        if (defaultCity) {
          setSelectedCity(defaultCity._id);
        } else if (activeCities.length > 0) {
          setSelectedCity(activeCities[0]._id);
        }
      })
      .catch((err) => console.error("Erreur chargement villes :", err));
  }, []);

  // Charger les statistiques
  const fetchStats = async () => {
    if (!selectedCity) return;
    try {
      setStatsLoading(true);
      const data = await synchronizationService.getDriversYandexStats(selectedCity);
      if (data) {
        setStats(data);
      }
    } catch (e) {
      console.error('Erreur chargement stats chauffeurs Yandex :', e);
    } finally {
      setStatsLoading(false);
    }
  };

  // Charger la liste des chauffeurs Yandex non présents dans Tigoon
  const fetchList = async () => {
    if (!selectedCity) return;
    try {
      setLoading(true);
      const params = { page, limit, cityId: selectedCity };
      if (q && q.trim()) {
        params.q = q.trim();
      }
      if (dateRange && dateRange[0]) params.start = dateRange[0].format('YYYY-MM-DD');
      if (dateRange && dateRange[1]) params.end = dateRange[1].format('YYYY-MM-DD');

      const data = await synchronizationService.getDriversNotInTigoon(params);

      if (!data) {
        setItems([]);
        setTotal(0);
        return;
      }

      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Erreur chargement chauffeurs Yandex :', e);
      message.error('Erreur lors du chargement de la liste');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, q, selectedCity, dateRange]);

  // Synchroniser un chauffeur Yandex vers Tigoon
  const handleSyncDriver = async (driverProfilId) => {
    if (!selectedCity) {
      message.error('Veuillez sélectionner une ville');
      return;
    }
    try {
      setSyncingDriverId(driverProfilId);
      // ✅ Envoi du cityId pour utiliser les bons credentials Yandex
      await synchronizationService.syncDriverFromYandex({ driverProfilId, cityId: selectedCity });
      message.success('Chauffeur synchronisé avec succès ! (1000 points attribués)');
      fetchList();
      fetchStats();
    } catch (error) {
      console.error('Erreur sync chauffeur :', error);
      const errorMsg = error?.message || 'Une erreur est survenue';
      message.error(errorMsg);
    } finally {
      setSyncingDriverId(null);
    }
  };

  // Synchroniser tous les chauffeurs Yandex
  const handleSyncAll = async () => {
    if (!selectedCity) {
      message.error('Veuillez sélectionner une ville');
      return;
    }
    try {
      setBulkSyncing(true);
      // ✅ Envoi du cityId pour utiliser les bons credentials Yandex
      const result = await synchronizationService.syncAllDriversFromYandex(selectedCity);
      message.success(`Synchronisation terminée : ${result.created || result.synced || 0} chauffeurs ajoutés avec 1000 points chacun`);
      if (result.errors > 0) {
        message.warning(`${result.errors} erreurs rencontrées`);
      }
      fetchList();
      fetchStats();
    } catch (error) {
      console.error('Erreur sync en masse :', error);
      message.error('Erreur lors de la synchronisation en masse');
    } finally {
      setBulkSyncing(false);
    }
  };

  // Configuration des status
  const statusConfig = {
    online: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'En ligne', icon: <WifiOutlined /> },
    busy: { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'Occupé', icon: <ClockCircleOutlined /> },
    offline: { color: '#8c8c8c', bg: '#fafafa', border: '#d9d9d9', label: 'Hors ligne', icon: null },
    free: { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', label: 'Disponible', icon: <CheckCircleOutlined /> }
  };

  // Colonnes du tableau
  const columns = [
    {
      title: 'Chauffeur',
      key: 'chauffeur',
      render: (_, record) => {
        const fullName = `${record.nom || ''} ${record.prenom || ''}`.trim() || 'N/A';
        const initials = getInitials(record.nom, record.prenom);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              size={isMobile ? 36 : 44}
              style={{ backgroundColor: getAvatarColor(fullName), fontWeight: 600 }}
            >
              {initials}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500, color: '#262626' }}>{fullName}</div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                {record.voiture || 'Véhicule non spécifié'}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Téléphone',
      key: 'telephone',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PhoneOutlined style={{ color: '#8c8c8c' }} />
          <span style={{ color: '#262626' }}>
            {record.telephone || record.phone || 'N/A'}
          </span>
        </div>
      ),
    },
    {
      title: 'Véhicule',
      key: 'voiture',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CarOutlined style={{ color: '#8c8c8c' }} />
          <div>
            <div style={{ fontWeight: 500, color: '#262626' }}>{record.voiture || 'N/A'}</div>
            {record.plaque && (
              <div style={{ fontSize: 12, color: '#8c8c8c', fontFamily: 'monospace' }}>
                {record.plaque}
              </div>
            )}
          </div>
        </div>
      ),
      responsive: ['md'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const config = statusConfig[status] || { color: '#8c8c8c', bg: '#fafafa', border: '#d9d9d9', label: status || 'N/A', icon: null };
        return (
          <Tag
            icon={config.icon}
            style={{
              backgroundColor: config.bg,
              color: config.color,
              border: `1px solid ${config.border}`,
              borderRadius: 6,
              padding: '2px 8px'
            }}
          >
            {config.label}
          </Tag>
        );
      },
      responsive: ['md'],
    },
    {
      title: 'Driver ID',
      dataIndex: 'driverProfilId',
      key: 'driverProfilId',
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#8c8c8c' }}>
            {text ? `${text.substring(0, 12)}...` : 'N/A'}
          </span>
        </Tooltip>
      ),
      responsive: ['lg'],
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      align: 'center',
      render: (_, record) => (
        <Can perm="sync:update">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size={controlSize}
            loading={syncingDriverId === record.driverProfilId}
            onClick={() => handleSyncDriver(record.driverProfilId)}
            style={{
              borderRadius: 8,
              background: 'linear-gradient(135deg, #eb2f96 0%, #f759ab 100%)',
              border: 'none'
            }}
          >
            {isMobile ? '' : 'Ajouter'}
            {!isMobile && <GiftOutlined style={{ marginLeft: 6 }} />}
          </Button>
        </Can>
      ),
    },
  ];

  // Gestion du changement de tableau
  const onTableChange = (pagination) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .sync-anciens-table .ant-table-thead > tr > th {
            background: #fafafa !important;
            font-weight: 600;
            color: #595959;
          }
          .sync-anciens-table .ant-table-tbody > tr:nth-child(odd) {
            background: #ffffff;
          }
          .sync-anciens-table .ant-table-tbody > tr:nth-child(even) {
            background: #fafafa;
          }
          .sync-anciens-table .ant-table-tbody > tr:hover > td {
            background: #fff0f6 !important;
          }
          .stat-card-anciens {
            border-radius: 12px;
            border: none;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06);
            transition: transform 0.2s, box-shadow 0.2s;
          }
          .stat-card-anciens:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#eb2f96' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#eb2f96' }}>Configuration</span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Anciens chauffeurs</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Cartes de statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-anciens"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Chauffeurs Yandex</span>}
                value={stats.totalYandex}
                prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-anciens"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #f6ffed 0%, #b7eb8f 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Dans Tigoon</span>}
                value={stats.totalTigoon}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-anciens"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Manquants</span>}
                value={stats.notInTigoon}
                prefix={<WarningOutlined style={{ color: '#fa8c16' }} />}
                valueStyle={{ color: '#fa8c16', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-anciens"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #fff0f6 0%, #ffadd2 100%)' }}
            >
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: '#595959', fontWeight: 500, fontSize: 14 }}>Taux de sync</span>
              </div>
              <Progress
                percent={stats.syncRate || 0}
                strokeColor={{
                  '0%': '#eb2f96',
                  '100%': '#ffadd2',
                }}
                format={(percent) => <span style={{ fontWeight: 600, color: '#eb2f96' }}>{Number(percent || 0).toFixed(1)}%</span>}
              />
            </Card>
          </Col>
        </Row>

        {/* Info Alert */}
        <Alert
          message={
            <span style={{ fontWeight: 500 }}>
              <GiftOutlined style={{ marginRight: 8 }} />
              Bonus de bienvenue : 1000 points
            </span>
          }
          description={
            <span>
              Cette liste affiche les chauffeurs présents dans <strong>Yandex</strong> mais absents de votre base <strong>Tigoon</strong>.
              Cliquez sur <strong>'Ajouter'</strong> pour synchroniser un chauffeur individuellement,
              ou <strong>'Tout synchroniser'</strong> pour les ajouter tous.
              <br /><br />
              <strong style={{ color: '#eb2f96' }}>Chaque ancien chauffeur synchronisé recevra automatiquement 1000 points de bienvenue.</strong>
            </span>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24, borderRadius: 12 }}
        />

        {/* Carte avec filtre et tableau */}
        <Card
          style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Barre de recherche et actions */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={8} md={5}>
              <Select
                placeholder="Sélectionner une ville"
                value={selectedCity}
                onChange={(val) => { setSelectedCity(val); setPage(1); }}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
                suffixIcon={<GlobalOutlined style={{ color: '#eb2f96' }} />}
              >
                {cities.map(city => (
                  <Select.Option key={city._id} value={city._id}>
                    {city.name} {city.isDefault && '(Par défaut)'}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={10} md={6}>
              <Input
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="Rechercher par nom, téléphone, véhicule..."
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                style={{ width: '100%', borderRadius: 8 }}
                allowClear
                size={controlSize}
              />
            </Col>
            <Col xs={24} sm={8} md={5}>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={(dates) => { setDateRange(dates); setPage(1); }}
                format="DD/MM/YYYY"
                placeholder={['Date début', 'Date fin']}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
                allowClear
              />
            </Col>
            <Col xs={24} sm={6} md={8} style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ color: '#8c8c8c', fontSize: 13 }}>
                <UserOutlined style={{ marginRight: 6 }} />
                {total} ancien{total > 1 ? 's' : ''}
              </div>
              <Button
                icon={<ReloadOutlined />}
                size={controlSize}
                onClick={() => { fetchList(); fetchStats(); }}
                style={{ borderRadius: 8 }}
              >
                Actualiser
              </Button>
              <Can perm="sync:update">
                <Popconfirm
                  title="Synchroniser tous les anciens chauffeurs ?"
                  description={`${stats.notInTigoon} chauffeur(s) seront ajoutés à Tigoon avec 1000 points chacun.`}
                  onConfirm={handleSyncAll}
                  okText="Oui, synchroniser"
                  cancelText="Annuler"
                  disabled={stats.notInTigoon === 0}
                >
                  <Button
                    type="primary"
                    icon={<CloudDownloadOutlined />}
                    size={controlSize}
                    loading={bulkSyncing}
                    disabled={stats.notInTigoon === 0}
                    style={{
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #eb2f96 0%, #f759ab 100%)',
                      border: 'none'
                    }}
                  >
                    Tout sync
                  </Button>
                </Popconfirm>
              </Can>
            </Col>
          </Row>

          {/* Tableau */}
          <Table
            className="sync-anciens-table"
            rowKey="driverProfilId"
            columns={columns}
            dataSource={items}
            loading={isLoading}
            onChange={onTableChange}
            size={isMobile ? 'small' : 'middle'}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              showTotal: (total, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {total}
                </span>
              ),
            }}
            locale={{
              emptyText: (
                <div style={{ padding: 40, textAlign: 'center' }}>
                  <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
                  <div style={{ color: '#52c41a', fontSize: 16, fontWeight: 500 }}>
                    Tous les anciens chauffeurs sont synchronisés !
                  </div>
                  <div style={{ color: '#8c8c8c', marginTop: 8 }}>
                    Aucun chauffeur Yandex en attente d'importation
                  </div>
                </div>
              )
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default SyncAnciensChauffeurs;
